// components/TrazabilidadSteps.jsx
import React, { useMemo, useState, useCallback } from "react";
import { Steps, Typography, Table, Space, Tag } from "antd5";
import dayjs from "dayjs";
import { ButtonItem, dineroDisabledMask, ModalItem } from "../../../../Components/Items";
import { useHistory } from "react-router-dom"; // ✅ v5 usa useHistory

const { Text } = Typography;

export default function TrazabilidadSteps({ trace, onOpen }) {
    // 🟦 Hooks
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedStep, setSelectedStep] = useState(null);
    const history = useHistory(); // 👈

    // 🔵 Util: resolver ruta y props según el step
    const openRegistro = useCallback((stepKey, row) => {
        if (!stepKey || !row?._id) return;
        const key = String(stepKey).toLowerCase();

        switch (key) {
            case "cotizacion":
            case "cotizaciones":
                history.push("/panel/verCotizacion", {
                    id: row._id,
                    from: "trazabilidad",
                    registro: row,
                });
                return;

            case "pedido":
            case "pedidos":
                history.push("/panel/verPedido", {
                    id: row._id,
                    from: "trazabilidad",
                    registro: row,
                });
                return;

            case "venta":
            case "ventas":
                history.push("/panel/verVenta", {
                    id: row._id,
                    from: "trazabilidad",
                    registro: row,
                });
                return;

            default:
                onOpen?.(stepKey, row._id);
                return;
        }
    }, [history, onOpen]);

    // 🟩 Columnas memorizadas
    const columns = useMemo(() => {
        return [
            {
                title: "Número",
                dataIndex: "numero",
                key: "numero",
                // `cancelada` solo lo manda buildTrace (cotizacion/pedido/venta); otros trace no lo traen
                render: (v, row) => (
                    <Space>
                        {v}
                        {row.cancelada && <Tag color="red">Cancelada</Tag>}
                    </Space>
                ),
            },
            {
                title: "Fecha",
                dataIndex: "fecha",
                key: "fecha",
                render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
            },
            { title: "Insumos", dataIndex: "insumosLength", key: "insumosLength" },
            {
                title: "Total",
                dataIndex: "totalPagar",
                key: "totalPagar",
                align: "right",
                render: (v) => dineroDisabledMask(v || 0),
            },
            {
                title: "Acciones",
                key: "actions",
                fixed: "right",
                render: (_, row) => (
                    <Space>
                        <ButtonItem
                            type="primary"
                            onClick={() => openRegistro(selectedStep?.key, row)} // 👈 push con props
                        >
                            Ver
                        </ButtonItem>
                    </Space>
                ),
            },
        ];
    }, [selectedStep?.key, openRegistro]); // onOpen ya no es necesario aquí

    // 🟥 Sin trazabilidad
    if (!trace || !trace.steps || !Array.isArray(trace.steps)) {
        return <Text type="secondary">No hay trazabilidad disponible</Text>;
    }

    const { steps, currentStepIndex } = trace;

    // 🟨 Click en Step
    const handleStepClick = (idx) => {
        const s = steps[idx];
        if (!s?.items?.length) return;
        setSelectedStep(s);
        setModalOpen(true);
    };

    // 🟦 Steps horizontales
    const stepItems = steps.map((s) => ({
        title: s.title,
        status: s.status,
        description:
            s.items?.length > 0 ? (
                <Text type="secondary">
                    {`${s.items.length} ${s.items.length === 1 ? "registro" : "registros"}`}
                </Text>
            ) : (
                <Text type="secondary">Sin registros</Text>
            ),
    }));

    return (
        <>
            <Steps
                direction="horizontal"
                current={currentStepIndex}
                items={stepItems}
                onChange={handleStepClick}
            />

            <ModalItem
                title={
                    selectedStep
                        ? `${selectedStep.title}: ${selectedStep.items?.length ?? 0} registro(s)`
                        : ""
                }
                visible={modalOpen}
                spinning={false}
                onCancel={() => setModalOpen(false)}
                footer={null}
                width={900}
            >
                {selectedStep?.items?.length ? (
                    <Table
                        rowKey={(r) => r._id}
                        dataSource={selectedStep.items}
                        columns={columns}
                        pagination={{ pageSize: 8 }}
                    />
                ) : (
                    <Text type="secondary">No hay registros para mostrar.</Text>
                )}
            </ModalItem>
        </>
    );
}
